import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Clock, LayoutDashboard, FileText } from 'lucide-react';
import { useDashboard } from './dashboardContext';
import CustomDomainSection from '../../CustomDomainSection';

const F_INPUT = 'w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[var(--accent-border)]';

// Requisites + pause + custom domain — rendered inside the unified SettingsSection
// ("Iestatījumi"). Identity / contacts / socials live in IdentityCard to avoid
// duplication; this block holds only the legal/operational settings.
export default function MoreSection() {
  const { activeTab, cfg, setCfg, legalSame, li, pauseBusy, saveBizSchedule, section, togglePause } = useDashboard();
  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const setLegal = (k, v) => setCfg({ ...cfg, legal_info: { ...(cfg?.legal_info || {}), [k]: v } });

  if (activeTab !== 'manage' || section !== 'settings' || !cfg?.template) return null;

  return (
    <>
      {/* Rekvizīti (juridiskā informācija) */}
      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden" data-testid="footer-info-card">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <FileText className="w-4 h-4 text-[var(--accent)]" />
          <p className="font-semibold text-white text-sm">Rekvizīti</p>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-xs text-white/40 -mt-1">Juridiskie rekvizīti šai biznesa lapai. Parādās publiskās lapas footerī un rēķinos.</p>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-white text-sm font-semibold">Rekvizīti tādi paši kā veikalam</p>
              <p className="text-white/40 text-xs">Izslēdz, lai šai lapai norādītu atsevišķus rekvizītus.</p>
            </div>
            <Switch checked={legalSame} onCheckedChange={(v) => setField('legal_same_as_shop', v)} data-testid="footer-legal-same-switch" />
          </div>
          {!legalSame && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="footer-legal-fields">
              <input value={li.legal_name || ''} onChange={(e) => setLegal('legal_name', e.target.value)} placeholder="Juridiskais nosaukums" className={F_INPUT} data-testid="footer-legal-name-input" />
              <input value={li.registration_number || ''} onChange={(e) => setLegal('registration_number', e.target.value)} placeholder="Reģ. numurs" className={F_INPUT} data-testid="footer-regnum-input" />
              <input value={li.vat_number || ''} onChange={(e) => setLegal('vat_number', e.target.value)} placeholder="PVN numurs" className={F_INPUT} data-testid="footer-vat-input" />
              <input value={li.legal_address || ''} onChange={(e) => setLegal('legal_address', e.target.value)} placeholder="Juridiskā adrese" className={F_INPUT} data-testid="footer-legal-address-input" />
            </div>
          )}
        </div>
      </div>

      {/* Pauze */}
      <div className={`p-4 sm:p-5 rounded-2xl border flex items-center gap-4 flex-wrap ${cfg?.is_paused ? 'bg-[var(--accent-soft)] border-[var(--accent-border)]' : 'bg-white/5 border-white/10'}`} data-testid="business-pause-card">
        <div className={`w-10 h-10 flex-shrink-0 rounded-xl flex items-center justify-center ${cfg?.is_paused ? 'bg-[var(--accent)]' : 'bg-emerald-500'}`}>
          {cfg?.is_paused ? <Clock className="w-5 h-5 text-white" /> : <LayoutDashboard className="w-5 h-5 text-white" />}
        </div>
        <div className="flex-1 min-w-[220px]">
          <p className="font-semibold text-white text-sm sm:text-base">
            {cfg?.is_paused ? 'Biznesa lapa ir pauzēta' : 'Biznesa lapa ir aktīva'}
          </p>
          <p className="text-xs sm:text-sm text-white/50 mt-0.5">
            {cfg?.is_paused
              ? 'Paslēpta no kartes. Publiskā lapa rāda “Pauzēts”.'
              : 'Pauzē, lai īslaicīgi paslēptu biznesa lapu no kartes.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-white/60">Pauzēt</span>
          <Switch checked={!!cfg?.is_paused} onCheckedChange={togglePause} disabled={pauseBusy} data-testid="business-pause-toggle" />
        </div>
        {cfg?.is_paused && (
          <div className="w-full flex items-center gap-3 flex-wrap pt-3 mt-1 border-t border-white/10" data-testid="business-pause-schedule">
            <span className="text-sm text-white/60">Atsākt automātiski:</span>
            <input
              type="date"
              value={(cfg?.pause_until || '').slice(0, 10)}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => saveBizSchedule(e.target.value)}
              className="text-sm border border-white/15 rounded-lg px-3 py-1.5 bg-white/5 text-white [color-scheme:dark]"
              data-testid="business-pause-until"
            />
            {cfg?.pause_until && (
              <button onClick={() => saveBizSchedule('')} className="text-xs text-white/50 underline" data-testid="business-pause-clear-date">Noņemt termiņu</button>
            )}
          </div>
        )}
      </div>

      {/* Pielāgotais domēns */}
      {cfg?.id && (
        <div data-testid="business-domain-card">
          <CustomDomainSection
            key={cfg.id}
            language="lv"
            dark
            basePath={`/business-page/me/pages/${cfg.id}/custom-domain`}
            target={{ accLV: 'KoLV biznesa lapu', nomLV: 'KoLV biznesa lapa', en: 'KoLV business page' }}
          />
        </div>
      )}
    </>
  );
}
